import React, { useCallback, useRef } from "react"
import styled from "~/ui/styled"
import { useTheme } from "~/ui/styled"
import { Text, Button, IconFont } from "~components/El"
import { alertWarning } from "~utils/alert"
import { getThumbnailAsync } from "~/ui/expo-video-thumbnails"
import type { PhotosModelProps } from "./photos-mode"

const Wrap = styled.div`
  width: 100%;
  height: 350px;
  box-sizing: border-box;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow: hidden;
`

const Grid = styled.div`
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  overflow-y: auto;
`

const Tile = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.background};
`

const Preview = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`

const VideoPreview = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  pointer-events: none;
`

const VideoBadge = styled.div`
  position: absolute;
  left: 6px;
  bottom: 6px;
  padding: 2px 6px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 11px;
  z-index: 1;
`

const PlayOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 2;
`

const PlayButton = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  border: 2px solid rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35);
  flex-shrink: 0;

  &::after {
    content: "";
    display: block;
    width: 0;
    height: 0;
    margin-left: 3px;
    border-style: solid;
    border-width: 7px 0 7px 12px;
    border-color: transparent transparent transparent #fff;
  }
`

const CheckMark = styled.div<{ $active?: boolean }>`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 22px;
  height: 22px;
  border-radius: 11px;
  border: 2px solid #fff;
  background: ${({ $active, theme }) => ($active ? theme.colors.primary : "rgba(0,0,0,0.35)")};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  cursor: pointer;
  z-index: 3;
`

const HiddenInput = styled.input`
  display: none;
`

async function fileToPhoto(file: File, index: number) {
  const uri = URL.createObjectURL(file)
  const isVideo = file.type.startsWith("video/")
  let previewUri = uri
  if (isVideo) {
    const thumb = await getThumbnailAsync({ uri }, { time: 100, quality: 0.75 })
    if (thumb.uri) previewUri = thumb.uri
  }
  return {
    id: `${file.name}-${file.lastModified}-${index}`,
    uri,
    path: uri,
    previewUri: isVideo && previewUri === uri ? "" : previewUri,
    mediaType: isVideo ? "video" : "photo",
    filename: file.name,
    size: file.size,
    type: file.type || (isVideo ? "video/mp4" : "image/jpeg"),
    content_type: file.type || (isVideo ? "video/mp4" : "image/jpeg"),
  }
}

const PhotosH5: React.FC<PhotosModelProps> = ({
  selectedPhotos,
  setSelectedPhotos,
}) => {
  const theme = useTheme()
  const inputRef = useRef<HTMLInputElement>(null)

  const openPicker = useCallback(() => {
    inputRef.current?.click()
  }, [])

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files?.length) return
      const picked = await Promise.all(Array.from(files).map((file, index) => fileToPhoto(file, index)))
      const merged = [...(selectedPhotos ?? []), ...picked]
      if (merged.length > 9) {
        alertWarning({ message: "最多选择9张图片!" })
        setSelectedPhotos?.(merged.slice(0, 9))
        return
      }
      setSelectedPhotos?.(merged)
    },
    [selectedPhotos, setSelectedPhotos],
  )

  const toggleSelect = useCallback(
    (photo: (typeof selectedPhotos)[0]) => {
      const list = selectedPhotos ?? []
      const index = list.findIndex((item) => item.id === photo.id)
      if (index >= 0) {
        setSelectedPhotos?.(list.filter((item) => item.id !== photo.id))
      } else if (list.length >= 9) {
        alertWarning({ message: "最多选择9张图片!" })
      } else {
        setSelectedPhotos?.([...list, photo])
      }
    },
    [selectedPhotos, setSelectedPhotos],
  )

  const photos = selectedPhotos ?? []

  return (
    <Wrap>
      <Button
        height="44px"
        borderRadius={theme.radii.lg}
        bg={theme.colors.background}
        onClick={openPicker}
      >
        <IconFont name="zhaopian" size={18} color={theme.colors.primary} />
        <Text color={theme.colors.secondaryText} fontSize={theme.fontSizes.md} ml="8px">
          从相册选择图片或视频
        </Text>
      </Button>

      <HiddenInput
        ref={inputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        onChange={(e) => {
          handleFiles(e.target.files)
          e.target.value = ""
        }}
      />

      {photos.length > 0 ? (
        <Grid>
          {photos.map((photo, index) => {
            const order = index + 1
            return (
              <Tile key={photo.id}>
                {photo.mediaType === "video" ? (
                  photo.previewUri ? (
                    <Preview src={photo.previewUri} alt={photo.filename} />
                  ) : (
                    <VideoPreview src={photo.uri} muted playsInline preload="metadata" />
                  )
                ) : (
                  <Preview src={photo.previewUri || photo.uri} alt={photo.filename} />
                )}
                {photo.mediaType === "video" && (
                  <>
                    <VideoBadge>视频</VideoBadge>
                    <PlayOverlay>
                      <PlayButton aria-hidden />
                    </PlayOverlay>
                  </>
                )}
                <CheckMark $active onClick={() => toggleSelect(photo)}>
                  {order}
                </CheckMark>
              </Tile>
            )
          })}
        </Grid>
      ) : (
        <Text color={theme.colors.secondaryText} fontSize={theme.fontSizes.sm}>
          支持多选，最多 9 个文件。选好后点右侧发送按钮。
        </Text>
      )}
    </Wrap>
  )
}

export default PhotosH5
